import { Avatar, Badge, Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, IconButton, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { Form } from '../../shared/forms';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Button } from '../../shared/components/MUI/button/Button';
import { LOCAL_STORAGE_KEY_USER_PROFILE_PICTURE, LOCAL_STORAGE_KEY_USER_NAME, LOCAL_STORAGE_KEY_USER_PHONE_CONFIRMED, useUserContext } from '../../shared/contexts';
import { VTextField, VTextFieldCPF, VTextFieldPhone } from '../../shared/forms';
import getValidationErrors from '../../shared/helpers/getValidationErrors';
import { LayoutShell } from '../../shared/layouts';
import { useAppThemeContext } from '../../shared/contexts';
import { authService, feedback, finishRequest, IUpdateUser, userService } from '../../shared/services';
import { Api } from '../../shared/services/api/axios-config';
import { setCsrfRequestToken } from '../../shared/services/api/axios-config/interceptors';
import { DatePicker, FileUploader } from '../../shared/components';

const schema = Yup.object().shape({
  name: Yup.string().required(),
  email: Yup.string().email().required(),
  cpf: Yup.string(),
  phone: Yup.string()
});

const passwordSchema = Yup.object().shape({
  oldPassword: Yup.string().required('Senha atual é obrigatória'),
  newPassword: Yup.string().required('Nova senha é obrigatória').min(6, 'Mínimo 6 caracteres'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Senhas não conferem')
    .required('Confirmação é obrigatória'),
});

export const Profile: React.FC = () => {
  const [userGender, setUserGender] = useState<number>();
  const [birthDate, setBirthDate] = useState<Dayjs | null>(null);
  const [openDialogUploadFile, setOpenDialogUploadFile] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [phoneConfirmModalOpen, setPhoneConfirmModalOpen] = useState(false);
  const [confirmCode, setConfirmCode] = useState('');

  const { userProfilePicture, setUserProfilePicture, setUserName, setPhoneConfirmed, whatsappSconappConfigured } = useUserContext();
  const { lgDown } = useAppThemeContext();

  const formRef = useRef<FormHandles>(null);
  const passwordFormRef = useRef<FormHandles>(null);

  const { data: user, refetch } = useQuery(
    ['user'],
    () => authService.getUserFromCookie()
  );

  const { mutate: updateUser, isLoading: isLoadingUpdateUser } = useMutation((user: IUpdateUser) =>
    userService.update(user), {
    onSuccess: () => finishRequest('Informações salvas', 'success'),
    onError: (error: Error) => finishRequest(error.message, 'error'),
  });

  const { mutate: requestPhoneCode, isLoading: isRequestingCode } = useMutation(
    (phone: string) => authService.requestPhoneConfirmation({ phone }),
    {
      onSuccess: () => {
        feedback('Código enviado para seu WhatsApp!', 'success');
        setPhoneConfirmModalOpen(true);
      },
      onError: (error: Error) => feedback(error.message, 'error'),
    }
  );

  const { mutate: confirmPhoneCode, isLoading: isConfirmingCode } = useMutation(
    (code: string) => authService.confirmPhone({ phone: user?.phone || '', code }),
    {
      onSuccess: () => {
        feedback('Telefone confirmado com sucesso!', 'success');
        setPhoneConfirmed(true);
        localStorage.setItem(LOCAL_STORAGE_KEY_USER_PHONE_CONFIRMED, JSON.stringify(true));
        setPhoneConfirmModalOpen(false);
        setConfirmCode('');
      },
      onError: (error: Error) => feedback(error.message, 'error'),
    }
  );

  const { mutate: changePassword, isLoading: isLoadingChangePassword } = useMutation(
    (data: { oldPassword: string; newPassword: string }) =>
      authService.changePassword({ oldPassword: data.oldPassword, newPassword: data.newPassword }),
    {
      onSuccess: () => {
        finishRequest('Senha alterada com sucesso', 'success');
        passwordFormRef.current?.reset();
        setPasswordModalOpen(false);
      },
      onError: (error: Error) => finishRequest(error.message, 'error'),
    }
  );

  const handleSubmit = useCallback(async (data: Record<string, unknown>) => {
    try {
      formRef.current?.setErrors({});
      await schema.validate({ name: data.name, email: data.email, cpf: data.cpf, phone: data.phone }, { abortEarly: false });

      if (user) {
        const userSubmit: IUpdateUser = {
          id: user.id,
          name: data.name as string,
          email: data.email as string,
          phone: data.phone as string | undefined,
          cpf: data.cpf as string | undefined,
          gender: userGender || 0,
          userRoles: user.userRoles,
          profilePicture: user.profilePicture,
          birthDate: birthDate ? birthDate.format('YYYY-MM-DD') : undefined,
        };

        updateUser(userSubmit);
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err as Yup.ValidationError);
        formRef.current?.setErrors(errors);
      }
    }
  }, [user, userGender, birthDate]);

  const handlePasswordSubmit = useCallback(async (data: { oldPassword: string; newPassword: string }) => {
    try {
      passwordFormRef.current?.setErrors({});
      await passwordSchema.validate(data, { abortEarly: false });
      changePassword(data);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err as Yup.ValidationError);
        passwordFormRef.current?.setErrors(errors);
      }
    }
  }, [changePassword]);

  useEffect(() => {
    if (user) {
      setUserGender(user.gender);
      setBirthDate(user.birthDate ? dayjs(user.birthDate) : null);
      localStorage.setItem(LOCAL_STORAGE_KEY_USER_PROFILE_PICTURE, JSON.stringify(user.profilePicture));
      setUserProfilePicture(user.profilePicture);
      localStorage.setItem(LOCAL_STORAGE_KEY_USER_NAME, JSON.stringify(user.name));
      setUserName(user.name);
    }
  }, [user]);

  // Refresh CSRF token when password dialog opens (token may be stale after login)
  useEffect(() => {
    if (passwordModalOpen) {
      Api.get('/auth/antiforgery-token').then((res) => {
        if (res.data) setCsrfRequestToken(res.data as string);
      }).catch(() => undefined);
    }
  }, [passwordModalOpen]);

  const onClose = () => {
    setOpenDialogUploadFile(false);
  };

  const onDelete = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY_USER_PROFILE_PICTURE, '');
    setUserProfilePicture(undefined);
  };

  return (
    <LayoutShell>
      {user &&
        <Box display='flex' flexDirection='column' gap={3}>
          <Form ref={formRef} onSubmit={handleSubmit} initialData={user}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper
                  variant='outlined'
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    padding: 3,
                    alignItems: lgDown ? 'stretch' : 'inherit'
                  }}
                >
                  <Box>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        <IconButton onClick={() => setOpenDialogUploadFile(true)}>
                          <Icon>photo_camera</Icon>
                        </IconButton>
                      }
                    >
                      <Avatar
                        src={userProfilePicture}
                        slotProps={{ img: { loading: 'lazy' } }}
                        sx={{ height: lgDown ? 100 : 150, width: lgDown ? 100 : 150 }}
                      />
                    </Badge>
                  </Box>

                  <VTextField name='name' label='Nome' required inputProps={{ 'data-testid': 'input-profile-name' }} />
                  <Box display='flex' flexDirection={lgDown ? 'column' : 'row'} gap={3}>
                    <VTextField name='email' label='E-mail' sx={{ flex: 1 }} required />
                    <Box display='flex' flexDirection='row' gap={1} alignItems='flex-start' sx={{ flex: 1 }}>
                      <VTextFieldPhone name='phone' label='Telefone' sx={{ flex: 1 }} />
                      {user.phone && !user.phoneConfirmed && whatsappSconappConfigured && (
                        <Button
                          label='Confirmar'
                          variant='outlined'
                          size='small'
                          onClick={() => requestPhoneCode(user.phone || '')}
                          isLoading={isRequestingCode}
                          sx={{ mt: 0.5, whiteSpace: 'nowrap', minWidth: 'auto', px: 1.5 }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Box display='flex' flexDirection={lgDown ? 'column' : 'row'} gap={3}>
                    <VTextFieldCPF name='cpf' label='CPF' sx={{ flex: 1 }} />
                    <Box sx={{ flex: 1 }}>
                      <DatePicker
                        label='Data de Nascimento'
                        value={birthDate}
                        onChange={setBirthDate}
                      />
                    </Box>
                  </Box>

                  <Paper variant='outlined' sx={{ p: 2, display: 'flex', flexDirection: lgDown ? 'column' : 'row', alignItems: 'center', gap: 2, alignSelf: lgDown ? 'stretch' : 'center' }}>
                    <Typography>Sexo:</Typography>
                    <ToggleButtonGroup
                      value={userGender}
                      exclusive
                      onChange={(_, value) => value != null && setUserGender(value)}
                      size={lgDown ? 'small' : 'medium'}
                    >
                      <ToggleButton value={1}>Masculino</ToggleButton>
                      <ToggleButton value={0}>Outro</ToggleButton>
                      <ToggleButton value={2}>Feminino</ToggleButton>
                    </ToggleButtonGroup>
                  </Paper>

                  <Box display='flex' justifyContent='end' gap={2} flexDirection={lgDown ? 'column-reverse' : 'row'}>
                    <Button
                      label='Alterar senha'
                      variant='outlined'
                      onClick={() => setPasswordModalOpen(true)}
                      minWidth={150}
                      fullWidth={lgDown}
                      data-testid='btn-change-password'
                    />
                    <Button
                      label='Salvar'
                      type='submit'
                      isLoading={isLoadingUpdateUser}
                      variant='contained'
                      minWidth={200}
                      fullWidth={lgDown}
                      data-testid='btn-save-profile'
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Form>

          {/* Phone Confirm Modal */}
          <Dialog open={phoneConfirmModalOpen} onClose={() => setPhoneConfirmModalOpen(false)} maxWidth='sm' fullWidth>
            <DialogTitle>Confirmar telefone</DialogTitle>
            <DialogContent>
              <Box display='flex' flexDirection='column' gap={2} mt={1}>
                <Typography variant='body2' color='text.secondary'>
                  Enviamos um código de confirmação via WhatsApp para {user.phone}. Digite o código abaixo:
                </Typography>
                <TextField
                  label='Código de confirmação'
                  value={confirmCode}
                  onChange={(e) => setConfirmCode(e.target.value)}
                  variant='outlined'
                  fullWidth
                  autoFocus
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button label='Cancelar' variant='outlined' onClick={() => { setPhoneConfirmModalOpen(false); setConfirmCode(''); }} />
              <Button
                label='Confirmar'
                variant='contained'
                onClick={() => confirmPhoneCode(confirmCode)}
                isLoading={isConfirmingCode}
                disabled={!confirmCode}
              />
            </DialogActions>
          </Dialog>

          {/* Password Change Modal */}
          <Dialog open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} maxWidth='sm' fullWidth>
            <Form ref={passwordFormRef} onSubmit={handlePasswordSubmit}>
              <DialogTitle>Alterar senha</DialogTitle>
              <DialogContent>
                <Box display='flex' flexDirection='column' gap={3} mt={1}>
                  <VTextField name='oldPassword' label='Senha atual' type='password' fullWidth required />
                  <VTextField name='newPassword' label='Nova senha' type='password' fullWidth required />
                  <VTextField name='confirmPassword' label='Confirmar nova senha' type='password' fullWidth />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button label='Cancelar' variant='outlined' onClick={() => setPasswordModalOpen(false)} />
                <Button label='Alterar senha' type='submit' isLoading={isLoadingChangePassword} variant='contained' data-testid='btn-save-password' />
              </DialogActions>
            </Form>
          </Dialog>

          <FileUploader
            open={openDialogUploadFile}
            id={user.id}
            refetch={refetch}
            uploadFile={userService.uploadFile}
            deleteFile={userService.deleteFile}
            onDelete={onDelete}
            onClose={onClose}
          />
        </Box>
      }
    </LayoutShell>
  );
};